import React from "react";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Investment, useInvestmentsId } from "@bn/api/apiv1";
import { LoadingPlaceHolder, Modal, Tabs, Tab } from "@components";
import { BookingWizardHeader } from "@features/transactionWizard/components/BookingWizardHeader";
import { InvestmentEditBasicTab } from "./components/InvestmentEditBasicTab";
import { InvestmentEditQuoteData } from "./components/InvestmentEditQuoteData";
import { InvestmentEditAdvancedTab } from "./components/InvestmentEditAdvancedTab";

export interface Props {
  investment: Investment | undefined;
  image?: string | null;
  isOpen: boolean;
  onClose: () => void;
  /** Tab to open on, e.g. "advanced" when coming from the missing-ISIN banner. */
  defaultTab?: string;
}

export const EditInvestmentModal = ({
  investment,
  image,
  isOpen,
  onClose,
  defaultTab,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const { data: fullInvestment, isPending } = useInvestmentsId(
    { id: investment?.id ?? 0 },
    { enabled: isOpen && !!investment?.id }
  );

  if (!isOpen || !investment) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("investment.header.actions.editInvestmentButton")}
      width={isMobile ? "auto" : 720}
      height="auto"
      maxHeight={isMobile ? "650px" : "100%"}
      position={isMobile ? "bottom" : "centered"}
      zIndex={10001}
      disablePortal={false}
    >
      <BookingWizardHeader
        name={investment.standardisedName || investment.name}
        isin={investment.isin}
        wkn={investment.wkn}
        type={investment.type}
        image={image}
      />

      <Box minHeight={isMobile ? undefined : 460}>
        {fullInvestment && !isPending ? (
          <Tabs defaultKey={defaultTab}>
            <Tab label={t("investmentEditPage.tabs.basic")} key="basic">
              <InvestmentEditBasicTab investment={fullInvestment} />
            </Tab>
            <Tab label={t("investmentEditPage.tabs.quoteData")} key="quoteData">
              <InvestmentEditQuoteData investment={fullInvestment} />
            </Tab>
            <Tab label={t("investmentEditPage.tabs.advanced")} key="advanced">
              <InvestmentEditAdvancedTab investment={fullInvestment} />
            </Tab>
          </Tabs>
        ) : (
          <LoadingPlaceHolder />
        )}
      </Box>
    </Modal>
  );
};
